import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NxMessageToastService } from '@aposin/ng-aquila/message';
import { SortDirection, SortEvent } from '@aposin/ng-aquila/table';
import { AppConfig } from 'src/config/app.config';
import { AuthService } from '../core/auth/auth.service';
import * as moment from 'moment';

@Component({
  selector: 'app-admin-pfu-invoice',
  templateUrl: './admin-pfu-invoice.component.html',
  styleUrls: ['./admin-pfu-invoice.component.css']
})
export class AdminPfuInvoiceComponent implements OnInit {

  /** Pagination */
  count: number = 0;
  pageNumber: number = 1;
  pageSize: number = 10;

  prevPage() { this.pageNumber--; this.getInvoiceList(); }
  nextPage() { this.pageNumber++; this.getInvoiceList(); }
  goToPage(n: number) { this.pageNumber = n; this.getInvoiceList(); }

  /** Sorting */
  sortTable(sort: SortEvent) {
    this.invoiceList = this.invoiceList.sort((a, b) => {
      if (sort.active in a && sort.active in b) {
        return this.compare(a[sort.active], b[sort.active], sort.direction);
      }
      return 0;
    });
  }

  compare(a: number | string | Date, b: number | string | Date, direction: SortDirection) {
    return (a < b ? -1 : 1) * (direction === 'asc' ? 1 : -1);
  }

  private _apiUrl: string;

  invoiceList: any[] = [];
  dealerList: any[] = [];
  searchTerm: string = '';
  dealerFilter: string = '';
  startDate: moment.Moment | null = null;
  endDate: moment.Moment | null = null;
  fileName: string = '';

  /** Upload modal */
  uploadModalOpen: boolean = false;
  uploadForm!: FormGroup;
  uploadedFilePath: string | null = null;
  submitting: boolean = false;

  /** Dealer dispute review modal */
  disputeModalOpen: boolean = false;
  disputeForm!: FormGroup;
  disputeInProgress: boolean = false;
  selectedInvoice: any = null;
  disputeDecision: 'Approved' | 'Rejected' | null = null;

  acceptedTypes: string = 'application/pdf';
  uploadPath: string = '/api/v1/Upload';

  readonly months: string[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  currentYear: number = new Date().getFullYear();
  years: number[] = [this.currentYear - 1, this.currentYear];

  constructor(
    private authService: AuthService,
    private appConfig: AppConfig,
    private fb: FormBuilder,
    private messageToastService: NxMessageToastService
  ) {
    this._apiUrl = appConfig.baseUrl;
  }

  ngOnInit(): void {
    this.uploadForm = this.fb.group({
      dealerId: [null, Validators.required],
      invoiceMonth: [null, Validators.required],
      invoiceYear: [this.currentYear, Validators.required],
      comments: ['', [Validators.required, Validators.maxLength(1000)]]
    });

    this.disputeForm = this.fb.group({
      adminComments: ['', [Validators.required, Validators.maxLength(1000)]]
    });

    this.getDealerList();
    this.getInvoiceList();
  }

    getDealerList() {
    this.authService
      .get(
        this._apiUrl +
        "/api/v1/Workshops/GetAllWorkshops").subscribe(
          (response) => {
            if (response) {
              this.dealerList = response;
            }
          },
          (error) => {
            console.error(error);
          }
        );
  }

  getInvoiceList() {
    const from = this.startDate ? this.startDate.format('LL') : '';
    const to = this.endDate ? this.endDate.format('LL') : '';
    this.authService
      .getList(
        this._apiUrl +
        `/api/v1/PfuInvoice/admin/list?pageNumber=${this.pageNumber}&pageSize=${this.pageSize}` +
        `&searchText=${this.searchTerm}&startDate=${from}&endDate=${to}` +
        (this.dealerFilter ? `&dealerId=${this.dealerFilter}` : '')
      )
      .subscribe(
        (response) => {
          if (response) {
            this.invoiceList = response.body || [];
            const paging = JSON.parse(response.headers.get('X-Pagination') || '{"TotalCount":0}');
            this.count = paging.TotalCount;
          }
        },
        (error) => console.error(error)
      );
  }

  exportToExcel() {
    const from = this.startDate ? this.startDate.format('LL') : '';
    const to = this.endDate ? this.endDate.format('LL') : '';
    this.authService
      .getList(
        this._apiUrl +
        `/api/v1/PfuInvoice/admin/list?pageNumber=-1&pageSize=10000` +
        `&searchText=${this.searchTerm}&startDate=${from}&endDate=${to}` +
        (this.dealerFilter ? `&dealerId=${this.dealerFilter}` : '')
      )
      .subscribe(
        (response) => {
          if (response) {
            const rows = (response.body || []).map((item: any) => ({
              'Reference No.': item.referenceNo || '',
              'Dealer': item.dealerName || '',
              'Invoice Period': `${this.months[item.invoiceMonth - 1]} ${item.invoiceYear}`,
              'Uploaded On': item.submittedOn ? new Date(item.submittedOn).toLocaleDateString('en-GB') : '',
              'AWP Comments': item.comments || '',
              'Dealer Comments': item.disputeComment || ''
            }));
            import('xlsx').then(xlsx => {
              const ws = xlsx.utils.json_to_sheet(rows);
              const wb = xlsx.utils.book_new();
              xlsx.utils.book_append_sheet(wb, ws, 'PFU Invoices');
              this.fileName = `AdminPFUInvoices_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.xlsx`;
              xlsx.writeFile(wb, this.fileName);
            });
          }
        },
        (error) => console.error(error)
      );
  }

  openUploadModal() {
    this.uploadForm.reset({ invoiceYear: this.currentYear });
    this.uploadedFilePath = null;
    this.uploadModalOpen = true;
  }

  closeUploadModal() {
    this.uploadModalOpen = false;
    this.uploadedFilePath = null;
  }

  onFileUploaded(event: any) {
    this.uploadedFilePath = event?.dbPath || null;
    if (this.uploadedFilePath) {
      this.messageToastService.open('File uploaded successfully.', { context: 'success', duration: 4000 });
    }
  }

  submitInvoice() {
    this.uploadForm.markAllAsTouched();
    if (this.uploadForm.invalid) return;
    if (!this.uploadedFilePath) {
      this.messageToastService.open('Please upload a PDF invoice before submitting.', { context: 'info', duration: 4000 });
      return;
    }

    this.submitting = true;
    const payload = {
      dealerId: this.uploadForm.value.dealerId,
      invoiceMonth: this.months.indexOf(this.uploadForm.value.invoiceMonth) + 1,
      invoiceYear: this.uploadForm.value.invoiceYear,
      comments: this.uploadForm.value.comments,
      filePath: this.uploadedFilePath
    };

    this.authService
      .post(this._apiUrl + '/api/v1/PfuInvoice/submit', payload)
      .subscribe(
        () => {
          this.submitting = false;
          this.uploadModalOpen = false;
          this.messageToastService.open('PFU invoice uploaded successfully.', { context: 'success', duration: 4000 });
          this.getInvoiceList();
        },
        (error) => {
          this.submitting = false;
          console.error(error);
          this.messageToastService.open('Submission failed. Please try again.', { context: 'info', duration: 4000 });
        }
      );
  }

  /** Dealer dispute review modal */
  openDisputeModal(item: any) {
    this.selectedInvoice = item;
    this.disputeDecision = null;
    this.disputeForm.reset();
    if (item.adminComments) {
      this.disputeForm.patchValue({ adminComments: item.adminComments });
    }
    this.disputeModalOpen = true;
  }

  closeDisputeModal() {
    this.disputeModalOpen = false;
    this.selectedInvoice = null;
    this.disputeDecision = null;
  }

  submitDisputeReview(decision: 'Approved' | 'Rejected') {
    this.disputeForm.markAllAsTouched();
    if (this.disputeForm.invalid) return;

    this.disputeDecision = decision;
    this.disputeInProgress = true;
    const payload = {
      id: this.selectedInvoice.id,
      status: decision,
      adminComments: this.disputeForm.value.adminComments,
      rejectionReason: decision === 'Rejected' ? this.disputeForm.value.adminComments : null
    };
    this.authService
      .put(this._apiUrl + `/api/v1/PfuInvoice/review/${this.selectedInvoice.id}`, payload)
      .subscribe(
        () => {
          this.disputeInProgress = false;
          this.disputeModalOpen = false;
          this.messageToastService.open(
            decision === 'Approved' ? 'Dispute approved successfully.' : 'Dispute rejected.',
            { context: 'success', duration: 4000 }
          );
          this.getInvoiceList();
        },
        (error) => {
          this.disputeInProgress = false;
          this.disputeDecision = null;
          console.error(error);
          this.messageToastService.open('Action failed. Please try again.', { context: 'info', duration: 4000 });
        }
      );
  }

  badgeType(status: string): string {
    switch (status) {
      case 'Paid': return 'positive';
      case 'Rejected': return 'critical';
      case 'Under Process': return 'active';
      default: return 'active';
    }
  }

  downloadInvoice(invoiceId: number, referenceNo: string) {
    this.authService
      .export(this._apiUrl + `/api/v1/PfuInvoice/download/${invoiceId}`)
      .subscribe((data) => saveAs(data, `${referenceNo}_pfu_invoice.pdf`));
  }
}
